/**
 * Die beiden Checklisten: Startbestückung, Lesen und die Überführung des
 * alten Formats.
 *
 * Bis zu den ergänzbaren Listen hielt das Dokument nur Wahrheitswerte je Name:
 * { vignettes: { Slowenien: true }, kit: { Thermostat: true } }. Die Einträge
 * selbst standen unveränderlich in seed.ts. Wer spontan durch ein Land fuhr,
 * das dort nicht aufgeführt war, konnte dessen Vignette nirgends eintragen -
 * genau der Fall, um den es hier geht. Jetzt liegen die Einträge im Dokument,
 * mit Reihenfolge und bei den Vignetten mit Preis und Notiz.
 *
 * Auf einer laufenden Installation liegt das alte Format, und es kommt jederzeit
 * über den Abgleich herein - auch Wochen später noch, von einem Gerät, das
 * lange nicht online war. parseChecks versteht deshalb dauerhaft beide Formate
 * und steht an jeder Lesestelle: im Provider für das eigene Dokument, in
 * transfer.ts für eine Importdatei. Umgeschrieben wird nichts von selbst; die
 * nächste Änderung des Nutzers speichert ohnehin das neue Format.
 */

import { asList, isRecord, str } from './json'
import { KIT, VIGNETTES } from './seed'
import type { Checks, KitCheck, VignetteCheck } from './types'

/**
 * Die Checklisten der Beispielreise: die recherchierten Vignetten und die
 * Ersatzteilkiste als Ausgangspunkt, nichts abgehakt.
 *
 * Eine Funktion und keine Konstante, damit niemand versehentlich die Vorlage
 * selbst bearbeitet - die Listen sind jetzt veränderlich.
 *
 * Angelegt wird das nur noch auf ausdrücklichen Wunsch beim Anlegen einer
 * Reise (data/beispiel.ts). Eine Reise über Portugal braucht keine Vignette
 * für Bulgarien.
 */
export function seedChecks(): Checks {
  return { vignettes: seedVignettes(), kit: seedKit() }
}

/** Zwei leere Listen - die Checklisten einer frisch angelegten Reise. */
export function emptyChecks(): Checks {
  return { vignettes: [], kit: [] }
}

/** Ein leerer Vignetteneintrag, wie ihn "Vignette hinzufügen" anlegt. */
export function emptyVignette(): VignetteCheck {
  return { country: '', price: '', note: '', done: false }
}

/** Ein leerer Eintrag der Ersatzteilliste. */
export function emptyKitItem(): KitCheck {
  return { name: '', done: false }
}

/**
 * Liest ein checks-Dokument, egal in welchem Format es vorliegt.
 *
 * Entschieden wird je Liste und nicht für das ganze Dokument: ein Bestand kann
 * genau dazwischen liegen, wenn zwischen zwei Änderungen die Fassung wechselte.
 *
 * Kein Dokument heisst keine Listen und nicht "die Balkanvorlage". Hier stand
 * einmal das Gegenteil, und das war die zweite Stelle, an der eine neu
 * angelegte Reise zur Balkanreise wurde: die Startbestückung kam schon beim
 * Lesen zurück, ganz gleich, ob sie je angelegt worden war. Der Bestand der
 * bestehenden Reise ist davon nicht betroffen - dort liegt ein Dokument, im
 * alten wie im neuen Format, und beide Wege darunter sind unverändert.
 */
export function parseChecks(raw: unknown): Checks {
  if (!isRecord(raw)) return emptyChecks()
  return { vignettes: parseVignettes(raw.vignettes), kit: parseKit(raw.kit) }
}

function parseVignettes(raw: unknown): VignetteCheck[] {
  // Neues Format. Eine leere Liste ist ausdrücklich eine Aussage - der Nutzer
  // hat alles entfernt - und wird nicht mit der Startbestückung überschrieben.
  const liste = asList(raw)
  if (liste) {
    return liste.filter(isRecord).map((entry) => ({
      // Leere Namen bleiben erhalten: ein frisch angelegter Eintrag ist genau
      // das, und würde er beim Lesen verworfen, verschwände er der Oberfläche
      // unter den Fingern wieder.
      country: str(entry.country),
      price: str(entry.price),
      note: str(entry.note),
      done: Boolean(entry.done),
    }))
  }
  // Altes Format: Haken je Name. Dort standen die Einträge nicht im Dokument,
  // sie kommen aus der Vorlage - siehe migrateVignettes.
  if (isRecord(raw)) return migrateVignettes(raw)
  // Feld fehlt oder ist kaputt: keine Liste. Nicht die Vorlage - die gehört
  // ins Anlegen und nicht ins Lesen.
  return []
}

function parseKit(raw: unknown): KitCheck[] {
  const liste = asList(raw)
  if (liste) {
    return liste.filter(isRecord).map((entry) => ({
      name: str(entry.name),
      done: Boolean(entry.done),
    }))
  }
  if (isRecord(raw)) return migrateKit(raw)
  return []
}

/**
 * Überführt die alten Haken in Einträge.
 *
 * Die Einträge selbst sind im alten Format nicht enthalten, also kommen sie aus
 * der Startbestückung - mit ihrer Reihenfolge, ihren Preisen und Notizen. Die
 * gesetzten Haken werden über den Namen zugeordnet; kein Haken darf dabei
 * verloren gehen.
 *
 * Ein Name, den die Startbestückung nicht kennt, hängt hinten dran. Das kommt
 * vor, wenn die recherchierte Liste sich seit dem Abhaken geändert hat, und ein
 * gesetzter Haken ist Arbeit des Nutzers - er wiegt schwerer als eine saubere
 * Reihenfolge. Preis und Notiz stehen dann leer, denn das alte Format hat sie
 * nie gespeichert.
 *
 * Ein unbekannter Name ohne Haken wird nicht mitgenommen. Er trägt keine
 * Auskunft - nicht abgehakt ist der Normalfall - und das alte Format hat ihn
 * ohnehin nur dann gespeichert, wenn er einmal gesetzt und wieder entfernt
 * wurde.
 */
function migrateVignettes(haken: Record<string, unknown>): VignetteCheck[] {
  const liste = VIGNETTES.map((v) => ({ ...v, done: Boolean(haken[v.country]) }))
  const bekannt = new Set(liste.map((v) => v.country))

  for (const [country, gesetzt] of Object.entries(haken)) {
    if (!gesetzt || bekannt.has(country)) continue
    liste.push({ country, price: '', note: '', done: true })
  }
  return liste
}

function migrateKit(haken: Record<string, unknown>): KitCheck[] {
  const liste = KIT.map((name) => ({ name, done: Boolean(haken[name]) }))
  const bekannt = new Set(liste.map((k) => k.name))

  for (const [name, gesetzt] of Object.entries(haken)) {
    if (!gesetzt || bekannt.has(name)) continue
    liste.push({ name, done: true })
  }
  return liste
}

function seedVignettes(): VignetteCheck[] {
  return VIGNETTES.map((v) => ({ ...v, done: false }))
}

function seedKit(): KitCheck[] {
  return KIT.map((name) => ({ name, done: false }))
}
